import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ColorPicker } from "@/components/colors/ColorPicker";

export const Route = createFileRoute("/colors")({
  head: () => ({
    meta: [
      { title: "Paint Color Picker — 1,700+ Sherwin-Williams Colors" },
      { name: "description", content: "Explore the full Sherwin-Williams ColorSnap and Emerald Designer Edition palette. Search, filter, and preview paint colors for your next project." },
      { property: "og:title", content: "Paint Color Picker — Sherwin-Williams" },
      { property: "og:description", content: "Search 1,700+ Sherwin-Williams paint colors by name, number, or family." },
      { property: "og:url", content: "https://michaelspaintingwi.com/colors" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/colors" }],
  }),
  component: ColorsRoute,
  errorComponent: ErrorView,
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Page not found.</div>,
});

function ErrorView({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <div className="py-32 text-center">
      <p className="text-zinc-300 mb-4">Couldn't load the color library.</p>
      <p className="text-zinc-500 text-sm mb-6">{error.message}</p>
      <button
        onClick={() => { router.invalidate(); reset(); }}
        className="bg-white text-zinc-950 px-5 py-2.5 rounded-full text-sm font-semibold"
      >Try again</button>
    </div>
  );
}

function ColorsRoute() {
  return (
    <section className="relative bg-[#030303] min-h-screen">
      <div className="absolute top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-amber-500/15 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-yellow-500/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-24 relative">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] font-medium tracking-widest uppercase text-amber-200 mb-5">
            Color Library · Sherwin-Williams
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5 text-balance">
            Find the right color. <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-200">Down to the swatch.</span>
          </h1>
          <p className="text-zinc-400 leading-relaxed text-lg max-w-2xl">
            Browse 1,700+ official Sherwin-Williams colors — ColorSnap fan deck plus all five Emerald Designer Edition collections. Search by name, SW number, or color family.
          </p>
        </div>
        <ColorPicker />
      </div>
    </section>
  );
}